import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  navigateToReactAppRoute,
  ReactAppNavigationCancelledError,
  ReactAppNavigationRedirectedError,
} from '@github-ui/react-app-navigation'
import {softNavigate} from '@github-ui/soft-navigate'

import {type GlobalNavigationContext, refreshGlobalNavigation} from '../global-navigation'
import {
  getCurrentLocationPath,
  getCurrentSelectedLink,
  getGlobalNavContextRefreshDescriptor,
  getNavigationError,
  GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE,
  locationMatchesGlobalNavContext,
  reloadIfAtLocation,
  setCurrentGlobalNavContext,
} from './context-region-navigation-state'

export interface NavigateWithContextRefreshOptions {
  /** The mounted React app that should handle the navigation. */
  appName: string
  /** The context to show at the destination. Its `path` is the navigation destination. */
  destinationContext: GlobalNavigationContext
  /** The context currently displayed, restored by browser Back/Forward. */
  sourceContext: GlobalNavigationContext
  /** Additional router navigation state. */
  state?: Record<string, unknown>
}

/**
 * Navigates the mounted React app to a destination with a different global navigation context, and refreshes the
 * context region to match. Both history entries store their context so Back/Forward can restore it.
 *
 * Returns `false` without navigating when `global_nav_context_refresh` is disabled or the app can't handle the
 * destination, so the caller can fall back to a full navigation. After it returns `true`, a failed context refresh falls back to a full navigation of the
 * destination on its own.
 */
export function navigateWithContextRefresh({
  appName,
  destinationContext,
  sourceContext,
  state,
}: NavigateWithContextRefreshOptions): boolean {
  if (!isFeatureEnabled('global_nav_context_refresh')) return false

  const previousHistoryContext = getGlobalNavContextRefreshDescriptor()

  // Store the source context before navigating, because React Router can push the destination entry synchronously.
  setCurrentGlobalNavContext(sourceContext)
  const navigationCompletion = navigateToReactAppRoute(destinationContext.path, appName, {
    state: {...state, [GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE]: destinationContext},
  })
  if (!navigationCompletion) {
    // Turbo restores this entry on Back, so it doesn't need a descriptor.
    setCurrentGlobalNavContext(previousHistoryContext)
    return false
  }

  void refreshContextOrNavigate(destinationContext, navigationCompletion, sourceContext)
  return true
}

/**
 * Resolves the context displayed for the current history entry: one stored by an earlier context navigation, or
 * `crumb` at the current location when the rendered context region still matches it.
 */
export function getDisplayedContext(
  crumb: Pick<GlobalNavigationContext, 'crumbType' | 'crumbObjectIdentifier' | 'scope'>,
  {currentPath, selectedLink}: {currentPath?: string; selectedLink?: string},
): GlobalNavigationContext | undefined {
  const historyContext = getGlobalNavContextRefreshDescriptor()
  if (historyContext && locationMatchesGlobalNavContext(historyContext.path)) return historyContext

  const path = getCurrentLocationPath()
  const currentSelectedLink = getCurrentSelectedLink(selectedLink)
  if (!currentSelectedLink || !locationMatchesGlobalNavContext(currentPath ?? path, {ignoreHash: true})) {
    return undefined
  }

  return {...crumb, path, selectedLink: currentSelectedLink}
}

async function refreshContextOrNavigate(
  destinationContext: GlobalNavigationContext,
  navigationCompletion: Promise<void>,
  sourceContext: GlobalNavigationContext,
) {
  const destination = destinationContext.path
  const [result, navigationError] = await Promise.all([
    refreshGlobalNavigation({...destinationContext, navigationCompletion, currentContext: sourceContext}),
    getNavigationError(navigationCompletion),
  ])
  if (result !== 'failed' || navigationError instanceof ReactAppNavigationCancelledError) return

  // A redirect commits a route whose context was not requested, so reload it rather than keep the source context.
  if (navigationError instanceof ReactAppNavigationRedirectedError) {
    reloadIfAtLocation(navigationError.location)
  } else if (!reloadIfAtLocation(destination) && locationMatchesGlobalNavContext(sourceContext.path)) {
    softNavigate(destination)
  }
}
